export const CLAIM_STATUS_FINALISED = 'finalised';
export const CLAIM_STATUS_SUBMITTED = 'submitted';
export const CLAIM_STATUS_CLAIMING = 'claiming';
// TAV-2595. The four states a claim moves through once Services Australia has it - mirrors the
// API's SA_CLAIM_STATUS_TO_TAG. All four are locked (past the editable point) and claimed (Services
// Australia has the invoice, so no local transition exists - see isClaimClaimed);
// `cancelled`/`completed` are additionally terminal - see isClaimTerminal.
export const CLAIM_STATUS_PENDING_APPROVAL = 'pending_approval';
export const CLAIM_STATUS_APPROVED = 'approved';
export const CLAIM_STATUS_CANCELLED = 'cancelled';
export const CLAIM_STATUS_COMPLETED = 'completed';

const CLAIM_LOCKED_TAGS = new Set<string>([
  CLAIM_STATUS_FINALISED,
  CLAIM_STATUS_SUBMITTED,
  CLAIM_STATUS_CLAIMING,
  CLAIM_STATUS_PENDING_APPROVAL,
  CLAIM_STATUS_APPROVED,
  CLAIM_STATUS_CANCELLED,
  CLAIM_STATUS_COMPLETED,
]);

// FOUR axes over these tags, one per set in the API's src/constants/sah_claims.mjs. They are
// deliberately not interchangeable, and each has its own gate:
//   - locked   - past the editable point. No edit/delete/validate/finalise. The widest set.
//   - lodged   - the SA invoice has left Draft, so items can no longer be reconciled onto it.
//                Excludes `finalised`, which is locked but never sent - re-sending IS the retry for a
//                claim whose items did not all reach SA (the API admits it in checkSendableToSa).
//   - claimed  - Services Australia owns the claim and Claim 1.0.0 has no un-claim operation, so no
//                state action may be OFFERED. Excludes `finalised`/`submitted`, which unfinalise can
//                still walk back.
//   - terminal - the VENDOR's own axis: no further SA transition at all.
// They nest: terminal ⊂ claimed ⊂ lodged ⊂ locked, each strictly.

// Mirrors the API's CLAIM_LODGED_STATUS_TAGS - what `checkSendableToSa` refuses on. Gates whether the
// push summary offers Send/Re-send at all: below this, an item edit can still be reconciled onto the
// invoice; at or past it, Services Australia refuses item edits and there is nothing left to send.
const CLAIM_LODGED_TAGS = new Set<string>([
  CLAIM_STATUS_SUBMITTED,
  CLAIM_STATUS_CLAIMING,
  CLAIM_STATUS_PENDING_APPROVAL,
  CLAIM_STATUS_APPROVED,
  CLAIM_STATUS_CANCELLED,
  CLAIM_STATUS_COMPLETED,
]);

// Mirrors the API's CLAIM_CLAIMED_STATUS_TAGS - what `unfinalise` refuses on (400).
const CLAIM_CLAIMED_TAGS = new Set<string>([
  CLAIM_STATUS_CLAIMING,
  CLAIM_STATUS_PENDING_APPROVAL,
  CLAIM_STATUS_APPROVED,
  CLAIM_STATUS_CANCELLED,
  CLAIM_STATUS_COMPLETED,
]);

const CLAIM_TERMINAL_TAGS = new Set<string>([CLAIM_STATUS_CANCELLED, CLAIM_STATUS_COMPLETED]);

/**
 * Past the editable point: finalised, lodged with Services Australia, being claimed by them, or
 * anywhere in the Services Australia claim lifecycle (pending approval / approved / cancelled /
 * completed).
 */
export const isClaimLocked = (tag?: string | null): boolean => (tag ? CLAIM_LOCKED_TAGS.has(tag) : false);
export const isClaimSubmitted = (tag?: string | null): boolean => tag === CLAIM_STATUS_SUBMITTED;
export const isClaimClaiming = (tag?: string | null): boolean => tag === CLAIM_STATUS_CLAIMING;
/**
 * Services Australia has claimed the invoice, and Claim 1.0.0 offers no way back for ANY of these
 * five states - so the claim has no state transition a user can drive. Gates the no-action branch in
 * `Claim.tsx`'s renderClaimStateAction: without it a claim past `claiming` falls through to the
 * locked branch and is offered an Unfinalise the API refuses with a 400.
 *
 * Supersedes `isClaimClaiming` for that purpose. That predicate covered every claimed state only
 * while `claiming` was the sole one; TAV-2595 added four downstream tags, all of which need the same
 * refusal - the same widening the API made from `isClaimClaimingTag` to `isClaimClaimedTag`.
 */
export const isClaimClaimed = (tag?: string | null): boolean => (tag ? CLAIM_CLAIMED_TAGS.has(tag) : false);
/**
 * The Services Australia invoice has left Draft, so items can no longer be reconciled onto it and
 * there is nothing left to send. Gates the Send/Re-send action in `ClaimApiPushSummary`.
 *
 * `finalised` is deliberately OUT: it is locked but was never sent, and re-sending is the retry path
 * for a claim whose items did not all reach Services Australia. Everything from `submitted` onward is
 * in, matching the API's `CLAIM_LODGED_STATUS_TAGS` / `checkSendableToSa` - rendering a Send button
 * beside a claim SA has already claimed and paid offers an action that cannot succeed, and the
 * disabled-state copy that comes with it ("unfinalise the claim first") is advice the user cannot
 * follow, since unfinalise refuses a claimed claim too.
 */
export const isClaimLodged = (tag?: string | null): boolean => (tag ? CLAIM_LODGED_TAGS.has(tag) : false);
/** Cancelled or completed - the Services Australia claim lifecycle has ended for this claim. */
export const isClaimTerminal = (tag?: string | null): boolean => (tag ? CLAIM_TERMINAL_TAGS.has(tag) : false);
